<script setup>
import { computed } from 'vue'

const props = defineProps({
  etapaAtiva: {
    type: String,
    default: 'contrato'
  },
  // corEtapaAtiva: cor de fundo da etapa ativa (animação do ícone).
  // Passada por quem chama o componente. Quando não fornecida, usa a cor da
  // própria etapa (var(--step-color)) - comportamento legado (fluxo de contratos).
  corEtapaAtiva: {
    type: String,
    default: null
  },
  // etapaSelecionada: qual etapa o usuário está visualizando (navegação do drilldown).
  // Pode ser diferente de etapaAtiva quando o usuário navega para uma etapa já concluída.
  // Quando não fornecido, usa etapaAtiva (comportamento legado).
  etapaSelecionada: {
    type: String,
    default: null
  },
  idContrato: { type: [Number, String], default: null },
  idCota: { type: [Number, String], default: null },
  idEmbarque: { type: [Number, String], default: null },
  lstStatusEmbarque: { type: String, default: null },
  stages: {
    type: Array,
    default: () => [
      { key: 'contrato', label: 'Contrato', icon: 'bi-file-earmark-text', color: 'var(--bs-primary)' },
      { key: 'cota', label: 'Cotas / PO', icon: 'bi-cart-check', color: 'var(--bs-info)' },
      { key: 'embarque', label: 'Embarque', icon: 'bi-ship', color: 'var(--bs-warning)' },
      { key: 'aduana', label: 'Doc. Aduaneira', icon: 'bi-shield-check', color: 'var(--bs-danger)' },
      { key: 'estoque', label: 'Estocagem', icon: 'bi-box-seam', color: 'var(--bs-success)' }
    ]
  },
  // stageStatus: Array de strings com o estado de cada etapa da pipeline.
  // Valores possíveis: 'completed' | 'active' | 'available' | 'inactive'
  // Quando não fornecido, o componente deriva o estado de etapaAtiva + lstStatusEmbarque
  // (comportamento legado para compatibilidade com fluxo de contratos)
  stageStatus: {
    type: Array,
    default: null
  }
})

const emit = defineEmits(['navegar'])

const getEtapaIndex = (key) => props.stages.findIndex(e => e.key === key)
const indiceAtivo = computed(() => getEtapaIndex(props.etapaAtiva))

// Cor de fundo da etapa ativa: usa corEtapaAtiva quando fornecida,
// senão a cor da própria etapa (comportamento legado)
const corEtapaAtivaResolved = computed(() => props.corEtapaAtiva || 'var(--step-color)')

// Indica se o stageStatus foi fornecido (fluxo de embarques) ou se usa o
// fallback legado (fluxo de contratos - etapas independentes)
const isStageStatusExplicito = computed(() => !!props.stageStatus)

// Índice da etapa selecionada (baseado na navegação do drilldown)
// Usa etapaSelecionada quando fornecida, senão fallback para etapaAtiva (legado)
const etapaSelecionadaResolved = computed(() => props.etapaSelecionada || props.etapaAtiva)
const selectedIndex = computed(() => getEtapaIndex(etapaSelecionadaResolved.value))

// Obtém o status de uma etapa específica
// Se stageStatus foi fornecido como prop, usa ele.
// Caso contrário, deriva de etapaAtiva + lstStatusEmbarque (comportamento legado)
const getStageStatus = (index) => {
  // Se stageStatus foi passado como prop, usa ele
  if (props.stageStatus && props.stageStatus.length > index) {
    return props.stageStatus[index]
  }

  // Fallback: comportamento legado baseado em etapaAtiva + lstStatusEmbarque
  if (index < indiceAtivo.value) return 'completed'
  if (index === indiceAtivo.value) return 'active'
  if (index === indiceAtivo.value + 1) return 'available'
  return 'inactive'
}

// Lógica de navegação baseada no drill-down
// Regra: só permite navegar para etapas já concluídas ou a ativa (para trás).
// No modo legado (sem stageStatus - fluxo de contratos), a etapa 'available'
// (próxima etapa) também é clicável, pois cada etapa é um cadastro independente.
// No modo com stageStatus (fluxo de embarques), 'available' e 'inactive' são
// bloqueadas - o avanço é feito pelos botões de ação.
const navegarPara = (etapa) => {
  console.log('[ComexPipelineStatus] Navegando para:', etapa.key, 'Icon:', etapa.icon, 'Color:', etapa.color)
  const indexEtapaAlvo = getEtapaIndex(etapa.key)
  const status = getStageStatus(indexEtapaAlvo)

  // Só permite navegar para etapas concluídas (voltar) ou a ativa (etapa atual)
  // No modo legado, 'available' também é permitido (etapa seguinte liberada)
  if (status === 'completed' || status === 'active' || (status === 'available' && !isStageStatusExplicito.value)) {
    emit('navegar', etapa)
  }
}
</script>

<template>
  <div class="comex-pipeline-container mb-4">
    <div class="pipeline-steps">
      <div
        v-for="(etapa, index) in stages"
        :key="etapa.key"
        class="step-item"
        :class="{
          'active': getStageStatus(index) === 'active',
          // Auréola aplicada à etapa selecionada, inclusive quando é a etapa ativa
          'selected': index === selectedIndex,
          'completed': getStageStatus(index) === 'completed',
          // Etapas não alcançadas ficam bloqueadas e acinzentadas.
          // No modo legado (fluxo de contratos), 'available' permanece clicável.
          'disabled': getStageStatus(index) === 'inactive' || (getStageStatus(index) === 'available' && isStageStatusExplicito.value),
          // Indica que o fundo da etapa ativa é amarelo (exige texto escuro para contraste)
          'step-fundo-amarelo': corEtapaAtivaResolved === 'var(--bs-warning)'
        }"
        @click="navegarPara(etapa)"
      >
        <div
          class="step-icon-wrapper"
          :class="{ 'step-warning': etapa.color === 'var(--bs-warning)' }"
          :style="{ '--step-color': etapa.color, '--cor-etapa-ativa': corEtapaAtivaResolved, '--cor-selecionada': corEtapaAtivaResolved }"
        >
          <div class="step-icon">
            <!-- GUARDIÃO: O ícone bi-ship não existe nativamente no Bootstrap Icons instalado. Usando Lucide SVG conforme orientação do arquiteto -->
            <svg v-if="etapa.icon === 'bi-ship'" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-ship">
              <path d="M2 21c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1 .6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/>
              <path d="M19.38 20A11.6 11.6 0 0 0 21 14l-9-4-9 4c0 2.9.94 5.34 2.81 7.76"/>
              <path d="M19 13V7a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v6"/>
              <path d="M12 10v4"/>
              <path d="M12 2v3"/>
            </svg>
            <i v-else :class="['bi', etapa.icon]" style="display: inline-block !important; visibility: visible !important;"></i>
          </div>
          <div v-if="index < stages.length - 1" class="step-connector"></div>
        </div>
        <div class="step-label">{{ etapa.label }}</div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.comex-pipeline-container {
  padding: 1rem;
  background-color: var(--bs-tertiary-bg);
  border-radius: 0.5rem;
  border: 1px solid var(--bs-border-color);
}

.pipeline-steps {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  max-width: 900px;
  margin: 0 auto;
}

.step-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  cursor: pointer;
  transition: opacity 0.3s;
}

.step-item.disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

/* Etapas não alcançadas: ícone acinzentado, sem cor da etapa */
.step-item.disabled .step-icon {
  background-color: var(--bs-secondary-bg);
  border-color: var(--bs-border-color);
  color: var(--bs-secondary);
  box-shadow: none;
}

.step-item.disabled .step-label {
  color: var(--bs-secondary);
}

.step-item.disabled .step-connector {
  background-color: var(--bs-border-color);
}

.step-icon-wrapper {
  position: relative;
  z-index: 2;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.step-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: var(--bs-secondary-bg);
  border: 2px solid var(--bs-border-color);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  color: var(--bs-body-secondary);
  transition: all 0.3s ease;
}

/* Garantir que o SVG do Lucide (ícone ship) herde a cor do elemento pai para ter contornos brancos como os demais ícones */
.step-icon svg.lucide-ship {
  stroke: currentColor;
  color: inherit;
}

/* ============================================================ */
/* ETAPA ATIVA (baseada no status do backend)                   */
/* Cor sólida (definida por corEtapaAtiva), texto branco        */
/* A auréola só aparece quando a etapa está selecionada         */
/* ============================================================ */
.active .step-icon {
  background-color: var(--cor-etapa-ativa);
  border-color: var(--cor-etapa-ativa);
  color: white;
  transform: scale(1.1);
}

/* Fundo amarelo (etapa ativa amarela) exige texto escuro para contraste */
.step-item.active.step-fundo-amarelo .step-icon {
  color: #212529;
}

.step-item.active.step-fundo-amarelo .step-icon svg {
  color: #212529;
}

/* Garantir que o SVG do Lucide (ship) herde a cor do texto da etapa ativa */
.active .step-icon svg {
  color: inherit;
}

/* ============================================================ */
/* ETAPA SELECIONADA (etapa que o usuário está visualizando)    */
/* Auréola: borda mais grossa + brilho pulsante                 */
/* Aplica-se também quando a etapa selecionada é a ativa        */
/* Usa --cor-selecionada: corEtapaAtiva quando fornecida,       */
/* senão a cor da própria etapa (comportamento legado)          */
/* ============================================================ */
.selected .step-icon {
  border-color: var(--cor-selecionada);
  border-width: 3px;
  color: var(--cor-selecionada);
  box-shadow: 0 0 10px var(--cor-selecionada);
  animation: glow-selected 1.5s ease-in-out infinite;
}

@keyframes glow-selected {
  0%, 100% { box-shadow: 0 0 5px var(--cor-selecionada); }
  50% { box-shadow: 0 0 15px var(--cor-selecionada), 0 0 25px rgba(255,255,255,0.05); }
}

/* Etapa ativa E selecionada: fundo sólido da etapa ativa + auréola pulsante */
.active.selected .step-icon {
  background-color: var(--cor-etapa-ativa);
  border-color: var(--cor-etapa-ativa);
  color: white;
  box-shadow: 0 0 10px var(--cor-selecionada);
  animation: glow-selected 1.5s ease-in-out infinite;
}

.active.selected.step-fundo-amarelo .step-icon {
  color: #212529;
}

.active.selected.step-fundo-amarelo .step-icon svg {
  color: #212529;
}

.selected .step-label {
  color: var(--cor-selecionada);
  font-weight: 700;
}

/* ============================================================ */
/* ETAPA CONCLUÍDA                                               */
/* Apenas borda colorida, fundo neutro                          */
/* ============================================================ */
.completed .step-icon {
  background-color: var(--bs-body-bg);
  border-color: var(--step-color);
  color: var(--step-color);
}

/* ============================================================ */
/* CONECTORES                                                    */
/* ============================================================ */
.step-connector {
  position: absolute;
  height: 2px;
  background-color: var(--bs-border-color);
  width: calc(100% - 40px);
  left: calc(50% + 20px);
  top: 50%;
  z-index: 1;
}

.completed .step-connector,
.active .step-connector {
  background-color: var(--step-color);
}

.selected .step-connector {
  background-color: var(--cor-selecionada);
}

/* ============================================================ */
/* LABEL                                                         */
/* ============================================================ */
.step-label {
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--bs-body-secondary);
  text-align: center;
  transition: color 0.3s ease, font-weight 0.3s ease;
}

.active .step-label {
  color: var(--bs-body-emphasis);
  font-weight: 700;
}

.completed .step-label {
  color: var(--step-color);
  font-weight: 600;
}
</style>
